"use client";

import { useLocale } from "next-intl";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import { z } from "zod/v4";

import type { RouteManifestEntry } from "@/lib/seo/route-manifest";
import {
  buildLocalizedPageList,
  markdownCandidatesForPath,
} from "@/lib/webmcp/content-tools";
import { jsonToolResult } from "@/lib/webmcp/tool-result";
import { useWebMCPTool } from "@/lib/webmcp/use-webmcp-tool";

const MAX_MARKDOWN_BYTES = 100 * 1024;
const getPageContentInput = z.object({ path: z.string().min(1) }).strict();
const listPagesInput = z.object({}).strict();

async function fetchMarkdown(path: string): Promise<string> {
  const candidates = markdownCandidatesForPath(path);

  for (const candidate of candidates) {
    const response = await fetch(candidate, {
      headers: { Accept: "text/markdown, text/plain" },
    });
    if (!response.ok) continue;

    const content = await response.text();
    if (new TextEncoder().encode(content).byteLength > MAX_MARKDOWN_BYTES) {
      throw new Error("Page markdown exceeds the 100KB response limit");
    }
    return content;
  }

  throw new Error(`No markdown content found for ${path}`);
}

export function WebMCPProvider({
  lang,
  routeManifest,
}: {
  lang: string;
  routeManifest: RouteManifestEntry[];
}) {
  const pathname = usePathname();
  if (/^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?eligibility(?:\/|$)/.test(pathname)) {
    return null;
  }

  return <SiteTools lang={lang} routeManifest={routeManifest} />;
}

function SiteTools({
  lang,
  routeManifest,
}: {
  lang: string;
  routeManifest: RouteManifestEntry[];
}) {
  const locale = useLocale();
  const pages = useMemo(
    () => buildLocalizedPageList(routeManifest, lang || locale),
    [lang, locale, routeManifest]
  );

  const listPagesTool = useMemo(
    () => ({
      name: "list_pages",
      description:
        "List Wayflyer pages available in the current locale, including their Markdown URLs.",
      inputSchema: z.toJSONSchema(listPagesInput),
      annotations: { readOnlyHint: true },
      execute(input: Record<string, unknown>) {
        listPagesInput.parse(input);
        return jsonToolResult({ pages });
      },
    }),
    [pages]
  );

  const getPageContentTool = useMemo(
    () => ({
      name: "get_page_content",
      description:
        "Get a Wayflyer page as Markdown. Pass a same-site path returned by list_pages.",
      inputSchema: z.toJSONSchema(getPageContentInput),
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      async execute(input: Record<string, unknown>) {
        const { path } = getPageContentInput.parse(input);
        const content = await fetchMarkdown(path);
        return jsonToolResult({ path, content });
      },
    }),
    []
  );

  useWebMCPTool(listPagesTool);
  useWebMCPTool(getPageContentTool);
  return null;
}
